import React, { useEffect } from 'react';
import Navigation from './Navigation';
import Footer from './Footer';
import AccessibilityContent from './AccessibilityContent';
import PageSEO from './PageSEO'; // Import the SEO component

const WebAccessibility = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // SEO configuration for accessibility page
  const accessibilitySEO = {
    title: "Web Accessibility Statement | Kinder's Meats Deli BBQ & Catering",
    description: "Kinder's Meats is committed to ensuring digital accessibility for people with disabilities. Learn about our accessibility features, standards compliance, and how to request accommodations or report accessibility issues.",
    keywords: [
      "Kinder's Meats accessibility",
      "web accessibility statement",
      "ADA compliance",
      "WCAG compliance",
      "restaurant accessibility",
      "disability accommodations",
      "accessible website",
      "accessibility features",
      "assistive technology support"
    ],
    canonicalUrl: "/web-accessibility",
    noIndex: false, // Accessibility statements should be indexed for compliance
    ogImage: "https://kindersmeats.com/images/kinders-logo-og.jpg",
    customMeta: {
      'document:type': 'Accessibility Statement',
      'document:category': 'Legal Compliance',
      'document:effective-date': 'June 4, 2025',
      'accessibility:compliant': 'WCAG 2.1 AA',
      'robots': 'index, follow, noarchive'
    },
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://kindersmeats.com/web-accessibility#webpage",
          "url": "https://kindersmeats.com/web-accessibility",
          "name": "Web Accessibility Statement | Kinder's Meats Deli BBQ & Catering",
          "description": "Accessibility statement detailing Kinder's Meats commitment to digital accessibility and compliance with accessibility standards.",
          "isPartOf": {
            "@id": "https://kindersmeats.com/#website"
          },
          "about": {
            "@id": "https://kindersmeats.com/#organization"
          },
          "datePublished": "2025-06-04",
          "dateModified": "2025-06-04",
          "inLanguage": "en-US",
          "breadcrumb": {
            "@id": "https://kindersmeats.com/web-accessibility#breadcrumb"
          },
          "mainEntity": {
            "@id": "https://kindersmeats.com/web-accessibility#accessibilitystatement"
          }
        },
        {
          "@type": "DigitalDocument",
          "@id": "https://kindersmeats.com/web-accessibility#accessibilitystatement",
          "name": "Kinder's Meats Web Accessibility Statement",
          "description": "Official accessibility statement outlining digital accessibility commitments, standards compliance, and accommodation procedures",
          "dateCreated": "2025-06-04",
          "dateModified": "2025-06-04",
          "author": {
            "@id": "https://kindersmeats.com/#organization"
          },
          "publisher": {
            "@id": "https://kindersmeats.com/#organization"
          },
          "genre": "Accessibility Statement",
          "keywords": "web accessibility, ADA compliance, WCAG standards, disability accommodations, assistive technology",
          "inLanguage": "en-US",
          "copyrightHolder": {
            "@id": "https://kindersmeats.com/#organization"
          },
          "about": [
            {
              "@type": "Thing",
              "name": "WCAG Compliance",
              "description": "Web Content Accessibility Guidelines compliance measures"
            },
            {
              "@type": "Thing",
              "name": "Assistive Technology Support",
              "description": "Compatibility with screen readers and other assistive technologies"
            },
            {
              "@type": "Thing",
              "name": "Accessibility Features",
              "description": "Built-in accessibility features and accommodations"
            },
            {
              "@type": "Thing",
              "name": "Feedback Process",
              "description": "How to report accessibility issues and request accommodations"
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://kindersmeats.com/#organization",
          "name": "Kinder's Meats Deli BBQ & Catering",
          "url": "https://kindersmeats.com",
          "logo": {
            "@type": "ImageObject",
            "url": "https://kindersmeats.com/images/kinders-logo.png",
            "width": 300,
            "height": 100
          },
          "description": "Family-owned BBQ restaurant chain committed to serving all customers, including those with disabilities, across 7 California locations since 1946.",
          "address": {
            "@type": "PostalAddress",
            "addressRegion": "CA",
            "addressCountry": "US"
          },
          "accessibilityPolicy": "https://kindersmeats.com/web-accessibility",
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "accessibility support",
            "availableLanguage": ["English"],
            "description": "Contact for accessibility questions and accommodation requests"
          },
          "hasCredential": [
            {
              "@type": "EducationalOccupationalCredential",
              "name": "WCAG 2.1 AA Compliance",
              "description": "Web Content Accessibility Guidelines Level AA compliance commitment"
            }
          ]
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://kindersmeats.com/web-accessibility#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://kindersmeats.com"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Web Accessibility",
              "item": "https://kindersmeats.com/web-accessibility"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What accessibility standards does Kinder's follow?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Kinder's Meats strives to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA standards to ensure our website is accessible to people with disabilities."
              }
            },
            {
              "@type": "Question",
              "name": "How can I report an accessibility issue?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "If you encounter any accessibility barriers on our website, please contact us through our contact form or call your nearest location. We are committed to addressing accessibility concerns promptly."
              }
            },
            {
              "@type": "Question",
              "name": "What assistive technologies does your website support?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Our website is designed to be compatible with screen readers, keyboard navigation, voice recognition software, and other assistive technologies commonly used by people with disabilities."
              }
            },
            {
              "@type": "Question",
              "name": "Do you provide accommodations in your restaurants?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, all Kinder's Meats locations are committed to providing reasonable accommodations for customers with disabilities. Please contact your local restaurant to discuss specific accommodation needs."
              }
            },
            {
              "@type": "Question",
              "name": "How often do you review accessibility compliance?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We regularly review and test our website for accessibility compliance. We also welcome feedback from users to help identify areas for improvement in our digital accessibility efforts."
              }
            },
            {
              "@type": "Question",
              "name": "Can I request alternative formats for information?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, we can provide information in alternative formats when possible. Contact us to request menus, policies, or other information in formats that meet your accessibility needs."
              }
            }
          ]
        }
      ]
    }
  };
    
  return (
    <>
      {/* SEO Component */}
      <PageSEO {...accessibilitySEO} />
      
      {/* Main content with semantic HTML structure */}
      <main>
        <section className="theme-light section m-0 p-0" data-theme="light">
          <div className='is-flex is-flex-direction-column is-justify-content-start'>
            <Navigation 
              colorMode="colorWhite"
            />
            
            {/* Accessibility Content */}
            <article itemScope itemType="https://schema.org/DigitalDocument">
              <AccessibilityContent 
                className="content-space-160 is-10-mobile is-10-tablet is-10-desktop is-8-widescreen is-8-fullhd is-offset-1-mobile is-offset-1-tablet is-offset-1-desktop is-offset-2-widescreen is-offset-2-fullhd"
                title="Web Accessibility"
                effectiveDate="June 4, 2025"
              />
            </article>

            {/* Hidden FAQ Section for SEO */}
            <section className="faq-section" style={{ display: 'none' }} aria-hidden="true" itemScope itemType="https://schema.org/FAQPage">
              <h2>Web Accessibility FAQ</h2>
              
              <div itemScope itemType="https://schema.org/Question">
                <h3 itemProp="name">What accessibility standards does Kinder's follow?</h3>
                <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                  <div itemProp="text">
                    Kinder's Meats strives to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA standards to ensure our website is accessible to people with disabilities.
                  </div>
                </div>
              </div>

              <div itemScope itemType="https://schema.org/Question">
                <h3 itemProp="name">How can I report an accessibility issue?</h3>
                <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                  <div itemProp="text">
                    If you encounter any accessibility barriers on our website, please contact us through our contact form or call your nearest location. We are committed to addressing accessibility concerns promptly.
                  </div>
                </div>
              </div>

              <div itemScope itemType="https://schema.org/Question">
                <h3 itemProp="name">What assistive technologies does your website support?</h3>
                <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                  <div itemProp="text">
                    Our website is designed to be compatible with screen readers, keyboard navigation, voice recognition software, and other assistive technologies commonly used by people with disabilities.
                  </div>
                </div>
              </div>

              <div itemScope itemType="https://schema.org/Question">
                <h3 itemProp="name">Do you provide accommodations in your restaurants?</h3>
                <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                  <div itemProp="text">
                    Yes, all Kinder's Meats locations are committed to providing reasonable accommodations for customers with disabilities. Please contact your local restaurant to discuss specific accommodation needs.
                  </div>
                </div>
              </div>

              <div itemScope itemType="https://schema.org/Question">
                <h3 itemProp="name">How often do you review accessibility compliance?</h3>
                <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                  <div itemProp="text">
                    We regularly review and test our website for accessibility compliance. We also welcome feedback from users to help identify areas for improvement in our digital accessibility efforts.
                  </div>
                </div>
              </div>

              <div itemScope itemType="https://schema.org/Question">
                <h3 itemProp="name">Can I request alternative formats for information?</h3>
                <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                  <div itemProp="text">
                    Yes, we can provide information in alternative formats when possible. Contact us to request menus, policies, or other information in formats that meet your accessibility needs.
                  </div>
                </div>
              </div>
            </section>
            
            <Footer />
          </div>
        </section>
      </main>
    </>
  );
};

export default WebAccessibility;